import { useState, useRef, useCallback, useEffect } from 'react';
import { getWsUrl } from '@/lib/config';
export function useVoiceChat({ gameId, playerId }) {
    const [state, setState] = useState('disconnected');
    const [isMuted, setIsMuted] = useState(false);
    const [remoteStreams, setRemoteStreams] = useState(new Map());
    const [error, setError] = useState(null);
    const localStreamRef = useRef(null);
    const audioRefs = useRef(new Map());
    const wsRef = useRef(null);
    const peersRef = useRef(new Map());
    const disconnect = useCallback(() => {
        // Stop local stream
        localStreamRef.current?.getTracks().forEach((track) => track.stop());
        localStreamRef.current = null;
        // Close all peer connections
        peersRef.current.forEach((peer) => peer.close());
        peersRef.current.clear();
        // Close WebSocket
        wsRef.current?.close();
        wsRef.current = null;
        // Stop all audio elements
        audioRefs.current.forEach((audio) => {
            audio.srcObject = null;
            audio.remove();
        });
        audioRefs.current.clear();
        setRemoteStreams(new Map());
        setState('disconnected');
    }, []);
    // Cleanup on unmount
    useEffect(() => {
        return () => {
            disconnect();
        };
    }, [disconnect]);
    const createPeerConnection = useCallback(async (peerId, isInitiator, ws) => {
        const peer = new RTCPeerConnection({
            iceServers: [
                { urls: 'stun:stun.cloudflare.com:3478' },
                { urls: 'stun:stun.l.google.com:19302' },
            ],
        });
        // Add local stream tracks
        localStreamRef.current?.getTracks().forEach((track) => {
            if (localStreamRef.current) {
                peer.addTrack(track, localStreamRef.current);
            }
        });
        // Receive remote stream
        peer.ontrack = (event) => {
            const [stream] = event.streams;
            setRemoteStreams((prev) => new Map(prev).set(peerId, stream));
            // Create and play audio element
            let audio = audioRefs.current.get(peerId);
            if (!audio) {
                audio = new Audio();
                audio.autoplay = true;
                audioRefs.current.set(peerId, audio);
            }
            audio.srcObject = stream;
        };
        // Send ICE candidates
        peer.onicecandidate = (event) => {
            if (event.candidate) {
                ws.send(JSON.stringify({
                    type: 'ICE_CANDIDATE',
                    targetId: peerId,
                    candidate: event.candidate,
                }));
            }
        };
        peersRef.current.set(peerId, peer);
        // If initiator, create offer
        if (isInitiator) {
            const offer = await peer.createOffer();
            await peer.setLocalDescription(offer);
            ws.send(JSON.stringify({
                type: 'OFFER',
                targetId: peerId,
                sdp: offer.sdp,
            }));
        }
    }, []);
    const handleOffer = useCallback(async (fromId, sdp, ws) => {
        await createPeerConnection(fromId, false, ws);
        const peer = peersRef.current.get(fromId);
        await peer.setRemoteDescription({ type: 'offer', sdp });
        const answer = await peer.createAnswer();
        await peer.setLocalDescription(answer);
        ws.send(JSON.stringify({
            type: 'ANSWER',
            targetId: fromId,
            sdp: answer.sdp,
        }));
    }, [createPeerConnection]);
    const handleAnswer = useCallback(async (fromId, sdp) => {
        const peer = peersRef.current.get(fromId);
        if (peer) {
            await peer.setRemoteDescription({ type: 'answer', sdp });
        }
    }, []);
    const handleIceCandidate = useCallback(async (fromId, candidate) => {
        const peer = peersRef.current.get(fromId);
        if (peer) {
            await peer.addIceCandidate(new RTCIceCandidate(candidate));
        }
    }, []);
    const connect = useCallback(async () => {
        try {
            setState('connecting');
            setError(null);
            // Request microphone access
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                },
            });
            localStreamRef.current = stream;
            // WebSocket for signaling
            const ws = new WebSocket(getWsUrl(`/ws/voice/${gameId}`));
            ws.onopen = () => {
                ws.send(JSON.stringify({ type: 'JOIN', playerId }));
                setState('connected');
            };
            ws.onmessage = async (event) => {
                const message = JSON.parse(event.data);
                switch (message.type) {
                    case 'PEER_JOINED':
                        await createPeerConnection(message.peerId, true, ws);
                        break;
                    case 'PEER_LEFT': {
                        const peer = peersRef.current.get(message.peerId);
                        if (peer) {
                            peer.close();
                            peersRef.current.delete(message.peerId);
                        }
                        setRemoteStreams((prev) => {
                            const next = new Map(prev);
                            next.delete(message.peerId);
                            return next;
                        });
                        break;
                    }
                    case 'OFFER':
                        await handleOffer(message.fromId, message.sdp, ws);
                        break;
                    case 'ANSWER':
                        await handleAnswer(message.fromId, message.sdp);
                        break;
                    case 'ICE_CANDIDATE':
                        await handleIceCandidate(message.fromId, message.candidate);
                        break;
                }
            };
            ws.onerror = () => {
                setError('Verbindung fehlgeschlagen');
                disconnect();
            };
            ws.onclose = () => {
                if (state !== 'disconnected') {
                    disconnect();
                }
            };
            wsRef.current = ws;
        }
        catch (err) {
            console.error('Voice connect error:', err);
            setError('Mikrofon-Zugriff verweigert');
            setState('disconnected');
        }
    }, [gameId, playerId, createPeerConnection, handleOffer, handleAnswer, handleIceCandidate, disconnect, state]);
    const toggleMute = useCallback(() => {
        if (localStreamRef.current) {
            const audioTrack = localStreamRef.current.getAudioTracks()[0];
            if (audioTrack) {
                audioTrack.enabled = !audioTrack.enabled;
                setIsMuted(!audioTrack.enabled);
                setState(audioTrack.enabled ? 'connected' : 'muted');
            }
        }
    }, []);
    return {
        state,
        isMuted,
        remoteStreams,
        error,
        connect,
        disconnect,
        toggleMute,
    };
}
//# sourceMappingURL=useVoiceChat.js.map